import React, { useState } from 'react';
import { DatePicker, Form, InputNumber, Button, notification } from 'antd';
import { Rule } from 'antd/lib/form';
import { BaseInput } from '@app/components/common/inputs/BaseInput/BaseInput';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { BaseSelect, Option as BaseSelectOption } from '@app/components/common/selects/BaseSelect/BaseSelect';
import moment from 'moment';
import { updatecData } from '@app/api/prImportFreeSales.api';
import { notificationController } from '@app/controllers/notificationController';

interface DynamicFieldsProps {
  formFields: any[];
  form: any;
  isFormEditable: boolean;
  cdata?: boolean;
  pr_id?: string | null;
}

const NewtaskForm: React.FC<DynamicFieldsProps> = ({ formFields, form, isFormEditable, cdata, pr_id }) => {
  const [loading, setLoading] = useState(false);
  const editable = cdata ? true : isFormEditable;

  const mapValidationRules = (rules: string[]): Rule[] => {
    return rules
      .map<Rule | null>((rule) => {
        if (rule === 'required') {
          return { required: true, message: 'This field is required' };
        }
        if (rule.startsWith('max:')) {
          const maxLength = parseInt(rule.split(':')[1], 10);
          return { max: maxLength, message: `Maximum length is ${maxLength} characters` };
        }
        if (rule === 'numeric') {
          return { type: 'number', message: 'Must be a number' };
        }
        return null;
      })
      .filter((rule): rule is Rule => rule !== null);
  };

  const getFormItemProps = (field: any) => {
    const baseProps = {
      name: field.field_id,
      label: field.name,
      rules: mapValidationRules(field.rules),
    };

    if (field.input_type === 'DATE' && editable) {
      return {
        ...baseProps,
        getValueFromEvent: (date: any) => (date ? date.format('DD/MM/YYYY') : null),
        getValueProps: (value: any) => {
          if (!value) return { value: null };

          const momentValue = moment(value, 'DD/MM/YYYY');
          return {
            value: momentValue && momentValue.isValid() ? momentValue : null,
          };
        },
      };
    }

    return baseProps;
  };

  const renderFormField = (field: any) => {
    const value = form.getFieldValue(field.field_id);

    if (!editable) {
      if (field.input_type === 'DATE') {
        let displayValue = value;

        if (!value) {
          displayValue = '';
        } else if (typeof value === 'string') {
          const cleanedValue = value.replace(/\\\//g, '/');
          const parts = cleanedValue.split('/');
          if (parts.length === 3) {
            if (parts[0].length === 4) {
              displayValue = `${parts[2]}/${parts[1]}/${parts[0]}`;
            } else {
              displayValue = cleanedValue;
            }
          }
        } else if (moment.isMoment(value)) {
          displayValue = value.format('DD/MM/YYYY');
        } else if (value instanceof Date) {
          displayValue = moment(value).format('DD/MM/YYYY');
        } else {
          const momentValue = moment(value);
          displayValue = momentValue.isValid() ? momentValue.format('DD/MM/YYYY') : String(value);
        }
        return <BaseInput readOnly value={displayValue} />;
      }

      return <BaseInput readOnly value={value} />;
    }

    switch (field.input_type) {
      case 'TEXT':
        return <BaseInput placeholder={`Enter ${field.name}`} />;
      case 'TEXTAREA':
        return <BaseInput.TextArea placeholder={`Enter ${field.name}`} />;
      case 'DECIMAL':
      case 'NUMBER':
        return <InputNumber placeholder={`Enter ${field.name}`} style={{ width: '100%' }} />;
      case 'DROPDOWN':
        return (
          <BaseSelect placeholder={`Select ${field.name}`} style={{ width: '100%' }}>
            {field.options?.map((option: any) => (
              <BaseSelectOption key={option} value={option}>
                {option}
              </BaseSelectOption>
            ))}
          </BaseSelect>
        );
      case 'DATE':
        return <DatePicker placeholder={`Select ${field.name}`} style={{ width: '100%' }} format="DD/MM/YYYY" />;
      default:
        return <BaseInput placeholder={`Enter ${field.name}`} />;
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      const values = await form.validateFields();
      const { country_id, pr_product_id, pr_type, ...filteredValues } = values;

      console.log('Filtered Updated values:', filteredValues);

      const response = await updatecData(Number(pr_id), filteredValues);
      console.log('API response:', response);

      notificationController.success({ message: 'Certificate details updated successfully!' });
    } catch (err: any) {
      notificationController.error({ message: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {formFields.map((field) => (
        <BaseCol xs={24} md={8} lg={12} key={field.field_id}>
          <Form.Item {...getFormItemProps(field)}>{renderFormField(field)}</Form.Item>
        </BaseCol>
      ))}

      {cdata && (
        <BaseCol xs={24}>
          <div style={{ marginTop: 20, textAlign: 'right' }}>
            <Button type="primary" onClick={handleSubmit} loading={loading}>
              Update Certificate Details
            </Button>
          </div>
        </BaseCol>
      )}
    </>
  );
};

export default NewtaskForm;
